import type { RegistrationFormData, RegistrationResponse, OrderStatusResponse } from '../types/registration'

// Use the index.php?rest_route= form so the REST API works regardless of
// whether nginx/Apache rewrites pretty /wp-json/ URLs through to PHP.
// This is WordPress's officially supported fallback URL form.
const WP_BASE = import.meta.env.VITE_WP_BASE || '/wp'
const REST_NAMESPACE = '/mha/v1'

function buildUrl(endpoint: string, params?: Record<string, string>): string {
  const url = new URL(`${WP_BASE}/index.php`, window.location.origin)
  url.searchParams.set('rest_route', `${REST_NAMESPACE}${endpoint}`)
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v))
  }
  return url.toString()
}

// Abort a request if it hangs longer than this so the UI never waits forever.
const REQUEST_TIMEOUT_MS = 12000
// GET requests may be safely retried (idempotent). POST is never retried.
const MAX_RETRIES = 2

function fetchWithTimeout(url: string, init?: RequestInit): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
  return fetch(url, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer))
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function fetchAPI<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  const url = buildUrl(endpoint, params)
  let lastError: unknown
  // Retry only on network failure (incl. timeout) or 5xx server errors. GET is
  // idempotent, so this is safe; POST/postAPI are never retried.
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const res = await fetchWithTimeout(url)
      if (!res.ok) {
        if (res.status >= 500 && attempt < MAX_RETRIES) {
          lastError = new Error(`API error: ${res.status}`)
          await delay(300 * (attempt + 1))
          continue
        }
        throw new Error(`API error: ${res.status}`)
      }
      return res.json()
    } catch (err) {
      lastError = err
      if (attempt < MAX_RETRIES) {
        await delay(300 * (attempt + 1))
        continue
      }
      throw err
    }
  }
  throw lastError
}

async function postAPI<T>(endpoint: string, body: unknown): Promise<T> {
  // No retry: re-posting could double-submit a registration / payment.
  const res = await fetchWithTimeout(buildUrl(endpoint), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({}))
    // Only surface a server message when it's a real, friendly string. Raw
    // low-level/unknown payloads (objects, empty/blank strings) are not leaked
    // verbatim to the user — fall back to a generic status message instead.
    const serverMessage =
      typeof error?.message === 'string' && error.message.trim() ? error.message : null
    throw new Error(serverMessage || `API error: ${res.status}`)
  }
  return res.json()
}

export interface BootstrapResponse {
  camps: import('../types/camp').CampData[]
  testimonials: import('../types/wp').Testimonial[]
  faqs: import('../types/wp').FAQ[]
  schedule: import('../types/wp').ScheduleItem[]
  heroes: import('../types/wp').HeroMember[]
  activities: import('../types/wp').Activity[]
  siteImages: import('../types/wp').SiteImages | null
  transportCities: import('../types/wp').TransportCity[]
  gallery: import('../types/wp').GalleryPhoto[]
}

// Single round-trip that returns all homepage content at once. Replaces the
// 8 separate REST calls (each of which booted WordPress from scratch).
export async function fetchBootstrap() {
  return fetchAPI<BootstrapResponse>('/bootstrap')
}

export async function submitRegistration(data: RegistrationFormData & { locale: string }) {
  return postAPI<RegistrationResponse>('/register', data)
}

export async function fetchOrderStatus(orderId: number, key: string) {
  return fetchAPI<OrderStatusResponse>(`/order-status/${orderId}`, { key })
}

export interface ContactFormData {
  name: string
  email: string
  phone?: string
  campType?: string
  message: string
  // Honeypot — must stay empty; bots that fill it are dropped server-side.
  company?: string
}

export async function submitContact(data: ContactFormData) {
  return postAPI<{ ok: boolean }>('/contact', data)
}
